import React, { useCallback, useEffect, useState } from "react";
import { FlaskConical, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Admin-only toggle to seed/clear clearly-labeled sample data in the
 * production workspace. Always isolated from the demo sandbox.
 */
export default function SampleDataToggle({ onChange }) {
  const [status, setStatus] = useState({ loaded: false, count: 0 });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/sample-data/status");
      setStatus(res.data);
    } catch {
      // Stay silent — non-admin or no permission. Component will hide via parent.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleToggle() {
    if (busy) return;
    setBusy(true);
    try {
      if (status.loaded) {
        await api.post("/sample-data/clear");
        toast.success("Sample data removed");
      } else {
        await api.post("/sample-data/load");
        toast.success("Sample data loaded — look for [Sample] in your client list");
      }
      await refresh();
      if (typeof onChange === "function") await onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not toggle sample data");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return null;

  return (
    <Button
      data-testid="sample-data-toggle"
      variant="outline"
      onClick={handleToggle}
      disabled={busy}
      className="rounded-2xl"
      title={status.loaded ? "Remove the [Sample] dogs from your workspace" : "Add 5 labeled sample dogs so you can explore features"}
    >
      {busy ? (
        <Loader2 size={16} className="mr-2 animate-spin" />
      ) : (
        <FlaskConical size={16} className="mr-2" />
      )}
      {status.loaded ? `Clear sample data (${status.count})` : "Load sample data"}
    </Button>
  );
}
